import { validateHTTPSUrl, validateScopes } from '../client/validations';
import { LocalStorageOIDC } from '../storage';
import { listenForPostMessage } from '../utils/browser';
import { generateNonce } from '../utils/nonce';
import { generateCodeChallenge, generateCodeVerifier } from '../utils/pkce';
import { decodeJwt } from 'jose';
import ky, { HTTPError } from 'ky';
import log from 'loglevel';
/**
 * AuthiculateClient is a class that provides methods to interact with the OIDC Authorization flow
 */
export class AuthiculateClient {
    options;
    //TODO: After server api routes refactoring, apiEndpoint property should be removed or merged with issuer
    apiEndpoint;
    logger;
    /**
     * Storage object used to store and retrieve OIDC data
     */
    storage;
    constructor(options) {
        this.options = options;
        this.storage = options.storage ?? new LocalStorageOIDC(options.storageOptions?.keyPrefix);
        this.logger = options.logger ?? log.getLogger('authiculate-sdk');
        validateHTTPSUrl(options.issuer, 'Issuer');
        validateHTTPSUrl(options.redirectUri, 'Redirect URI');
        this.apiEndpoint = options.apiUrl ?? new URL(options.issuer).origin.toString();
        validateHTTPSUrl(this.apiEndpoint, 'API URL');
    }
    async fetchTokens(body) {
        try {
            const { access_token, id_token, refresh_token } = await ky.post(`${this.options.issuer}/v1/token`, {
                body: new URLSearchParams(body).toString(),
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                method: 'POST',
            }).json();
            const accessTokenPayload = decodeJwt(access_token);
            const idTokenPayload = decodeJwt(id_token);
            this.storage.setAccessToken(access_token);
            this.storage.setAccessTokenPayload(accessTokenPayload);
            this.storage.setIdToken(id_token);
            this.storage.setIdTokenPayload(idTokenPayload);
            if (refresh_token) {
                this.storage.setRefreshToken(refresh_token);
            }
            const nonce = this.storage.getNonce();
            if (nonce && idTokenPayload['nonce'] !== nonce) {
                throw new Error('Nonce mismatch');
            }
            // Clears all the one-time-use keys from Storage
            this.storage.clearAuthorizationData();
            return {
                accessToken: access_token,
                accessTokenPayload,
                idToken: id_token,
                idTokenPayload,
                refreshToken: refresh_token,
            };
        }
        catch (err) {
            this.storage.clearAuthorizationData();
            this.logger.error(err);
            throw new Error('Failed to fetch tokens', { cause: err });
        }
    }
    handle404Error(error) {
        // Returns null for invalid sessions instead of rejecting
        if (error instanceof HTTPError && error.response.status === 404) {
            return null;
        }
        throw error;
    }
    async prepareAuthorizationCodeRequest(options, pkce = true) {
        validateScopes(options.scopes);
        const state = options.state ?? generateNonce();
        const url = new URL(`${this.options.issuer}/v1/authorize`);
        if (pkce) {
            const codeVerifier = generateCodeVerifier();
            const codeChallenge = await generateCodeChallenge(codeVerifier);
            this.storage.setCodeVerifier(codeVerifier);
            url.searchParams.append('code_challenge', codeChallenge);
        }
        const nonce = generateNonce();
        this.storage.setNonce(nonce);
        url.searchParams.append('client_id', this.options.clientId);
        url.searchParams.append('code_challenge_method', 'S256');
        url.searchParams.append('nonce', nonce);
        url.searchParams.append('redirect_uri', this.options.redirectUri);
        url.searchParams.append('response_type', 'code');
        url.searchParams.append('scope', options.scopes.join(' '));
        url.searchParams.append('state', state);
        if (options.prompt)
            url.searchParams.set('prompt', options.prompt);
        return { state, url };
    }
    /**
     * Delete the current session
     */
    async deleteSession() {
        await ky.delete(`${this.apiEndpoint}/api/v1/sessions/me`, { credentials: 'include' })
            .catch((error) => this.handle404Error(error))
            .finally(() => {
            this.storage.clear();
        });
    }
    /**
     * Fetch the current session data
    */
    async fetchSession() {
        return await ky.get(`${this.apiEndpoint}/api/v1/sessions/me`, { credentials: 'include' })
            .json()
            .catch((error) => this.handle404Error(error));
    }
    async fetchTokensWithAuthorizationCode(code) {
        const code_verifier = this.storage.getCodeVerifier();
        if (!code_verifier)
            throw new Error('Code verifier not found');
        return await this.fetchTokens({
            client_id: this.options.clientId,
            code,
            code_verifier,
            grant_type: 'authorization_code',
            redirect_uri: this.options.redirectUri,
        });
    }
    async fetchTokensWithRefreshToken(refreshToken, scopes) {
        const refresh_token = refreshToken ?? this.storage.getRefreshToken();
        if (!refresh_token)
            throw new Error('Refresh token not found');
        return await this.fetchTokens({
            client_id: this.options.clientId,
            grant_type: 'refresh_token',
            refresh_token,
            ...(scopes && { scope: scopes.join(' ') }),
        });
    }
    /**
     * Initiates the authorization code flow with Iframe. PKCE is used by default (can be disabled)
     * Created invisible iframe and listens for postMessage event from the OIDC provider
     * returns the authorization code
     */
    async getAuthCodeWithIframe(options) {
        const { state, url } = await this.prepareAuthorizationCodeRequest(options);
        url.searchParams.append('response_mode', 'okta_post_message');
        let data;
        try {
            data = await listenForPostMessage(url, 5000, state);
        }
        catch (err) {
            this.logger.error(err);
            throw new Error('Failed to get authorization code with iframe', { cause: err });
        }
        const { code } = data;
        if (!code)
            throw new Error('Missing authorization code in postMessage data');
        return code;
    }
    /**
     * Initiates the authorization code flow with redirect. PKCE is used by default (can be disabled)
     */
    async getAuthCodeWithRedirect(options) {
        const { url } = await this.prepareAuthorizationCodeRequest(options);
        window.location.href = url.toString();
    }
    getLogger() {
        return this.logger;
    }
    getStorage() {
        return this.storage;
    }
    /**
     * Refresh the current session and return the updated session data
     */
    async refreshSession() {
        return await ky.post(`${this.apiEndpoint}/api/v1/sessions/me/lifecycle/refresh`, { credentials: 'include' })
            .json()
            .catch((error) => this.handle404Error(error));
    }
}
